// utils/entities.ts
import { computed, makeAutoObservable } from "mobx";

export class Session {
  constructor(
    id: string,
    name: string,
    type: "user" | "organization",
    role?: string,
    imageUrl?: string,
    planName?: string,
    planColor?: string,
    maxDate?: Date | string,
  ) {
    makeAutoObservable(this, {
      initials: computed,
    });
    this.id = id;
    this.name = name;
    this.type = type;
    this.role = role;
    this.imageUrl = imageUrl;
    this.planName = planName;
    this.planColor = planColor;
    this.maxDate = maxDate;
  }

  id: string;
  name: string;
  type: "user" | "organization" = "user";
  role?: string;
  imageUrl?: string;
  planName?: string;
  planColor?: string;
  maxDate?: Date | string;

  updateSession(
    id: string,
    name: string,
    type: "user" | "organization",
    role?: string,
    imageUrl?: string,
    planName?: string,
    planColor?: string,
    maxDate?: Date | string,
  ) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.role = role;
    this.imageUrl = imageUrl;
    this.planName = planName;
    this.planColor = planColor;
    this.maxDate = maxDate;
  }

  get initials() {
    return this.name
      ?.split(" ")
      .map((s) => s.substring(0, 1))
      .slice(0, 2)
      .join("");
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      role: this.role,
      imageUrl: this.imageUrl,
      planName: this.planName,
      planColor: this.planColor,
      maxDate: this.maxDate,
    };
  }

  static fromJSON(json: any) {
    return new Session(
      json.id,
      json.name,
      json.type,
      json.role,
      json.imageUrl,
      json.planName,
      json.planColor,
      json.maxDate,
    );
  }
}

export class Account {
  constructor() {
    makeAutoObservable(this, {
      fullName: computed,
    });
  }

  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  emailConfirmed: boolean;
  imageUrl?: string;
  phone?: string;
  blockDate?: Date | string;
  profiles?: Session[];
  role?: string;
  maxDate?: Date | string;

  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }
}

export type User = Account;
export class OrganizationUser extends Account {
  constructor() {
    super();
    makeAutoObservable(this);
  }

  organizationId: string;
  declare role: string; // Override role from Account to be non-optional
  organization?: Organization;
}

export class Organization {
  constructor() {
    makeAutoObservable(this);
  }

  id: string;
  userId: string;
  name: string;
  email?: string;
  address?: Address;
  website?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  twitterUrl?: string;
  imageUrl?: string;
  role?: Role;
  venueId?: number; // FK to Venues table (INT)
  vendorId?: number; // FK to Vendors table (INT)
  isDigifreeSubscribed?: boolean;
  isStripeConnectEnabled?: boolean;
  stripeConnectStatus?: string;
  stripeConnectAccountId?: string;
  isDeleted?: boolean;
}

export class OrganizationInvite {
  constructor() {
    makeAutoObservable(this);
  }

  id: string;
  userId?: string;
  email?: string;
  role?: string;
  acceptedOn?: Date;
}

export class Role {
  id: string;
  name: string;
}

export type RecurrenceData = {
  type:
    | "none"
    | "daily"
    | "weekly"
    | "monthly-day"
    | "monthly-weekday"
    | "monthly"
    | "annually"
    | "weekdays"
    | "biweekly"
    | "custom";
  interval?: number;
  unit?: "day" | "week" | "month" | "year";
  weekdays?: string[];
  daysOfWeek?: number[]; // Array of day numbers (0=Sunday, 6=Saturday)
  endType: "never" | "on" | "after";
  endDate?: moment.Moment | null;
  occurrences?: number | null;
  dayOfMonth?: number; // Day of month (1-31)
  month?: number; // Month (1-12) for annually
  weekOrdinal?: number; // Week ordinal (1-4, or -1 for last)
  dayOfWeek?: number; // Day of week (0=Sunday, 6=Saturday)
};

export class Event {
  id: string;
  name?: string;
  locationName?: string;
  categories?: Category[];
  location?: Location;
  dateRange?: {
    start?: moment.Moment | string; // Backend sends ISO string, we convert to moment
    end?: moment.Moment | string; // Backend sends ISO string, we convert to moment
  };
  timeZoneId?: string; // IANA tz id (e.g. "America/Chicago"); set once the event is DST-aware, else absent
  recurrence?: RecurrenceData | null;
  recurrenceRule?: string; // RRule string from backend
  isRecurring?: boolean; // Flag indicating if event is recurring
  nextOccurrence?: string | null; // ISO 8601 datetime string (UTC) of the next occurrence for recurring events
  description?: string;
  parkingInfo?: string;
  eventLink?: string;
  ticketLink?: string;
  ageRange?: {
    min: number;
    max: number;
  };
  bannerImageUrl?: string;
  pinHue?: string | "#ff0000";
  IconUrl?: string;
  organizationId?: string;
  organization?: Organization;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    organizationMemberships?: any[];
  };
  parentEventId?: string;
  venueId?: number; // Backend sends as number
  venue?: any; // Pre-fetched venue data
  vendorId?: number[]; // Backend sends as array of numbers (legacy)
  vendors?: { id: number; name: string; organizationId?: string }[]; // New API format: array of vendor objects
  pricing?: Pricing[];
  isPaid?: boolean;
  canBookNow?: boolean;
  bookingUnavailableReason?: string;
  isFollowed?: boolean; // Track if user is following this event
  isHidden?: boolean; // Track if user has hidden this event
  isAttended?: boolean; // Track if user is attending this event
  isDeleted?: boolean;
  facebookPostId?: string;
  linkedInPostId?: string;
}

export class Category {
  constructor(_id?: string) {
    makeAutoObservable(this);
    if (_id) this.id = _id;
  }
  id?: string;
  name!: string;
  parentCategoryId?: string;
  parentCategory?: Category;
  subcategories?: Category[];
  isDeleted?: boolean;
}

export class Pricing {
  constructor(description?: string, price?: number) {
    makeAutoObservable(this);
    this.description = description;
    this.price = price;
  }

  id?: string;
  price?: number;
  description?: string;
}

export class Location {
  constructor() {
    makeAutoObservable(this);
  }

  id: string;
  address: Address;
  coordinate: Coordinate;
}

export class Coordinate {
  constructor() {
    makeAutoObservable(this);
  }

  latitude?: number | string; // Backend sends as number, but we may store as string for compatibility
  longitude?: number | string; // Backend sends as number, but we may store as string for compatibility
}

export class Address {
  constructor() {
    makeAutoObservable(this);
  }

  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: "US" | "CA";
  rawAddress?: string;
  lookupFormat?: string; // Backend format: "Address City State Zip"
  htmlDisplay?: string;
  textDisplay?: string;
}

export class Order {
  constructor() {
    makeAutoObservable(this);
  }

  id: string;
  userId: string;
  organizationId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  clientIP?: string;
  couponCode?: string;
  couponAmount?: number;
  subtotal?: number;
  tax?: number;
  total?: number;
}

export class Coupon {
  constructor(type: string, amount: number, code: string) {
    makeAutoObservable(this, {});

    this.type = type;
    this.amount = amount;
    this.code = code;
  }

  id?: string;
  type?: string; // 'P' for percentage, 'F' for flat amount
  amount: number; // Percentage value (e.g., 20) or flat amount (e.g., 30)
  code?: string;
  active?: boolean;
  createdDate?: string;
  discountPercentage?: number; // Deprecated: use amount with type
  isValid?: boolean; // Deprecated: use active
  isActive?: boolean; // Deprecated: use active
  expiryDate?: string;
}

export interface Feature {
  id: string;
  name: string;
  description: string;
  icon?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface Plan {
  id: string;
  name: string; // e.g., Basic, Pro, Enterprise
  description: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface PlanVersion {
  id: string;
  planId: string;
  price: number;
  currency: string;
  period: "month" | "year";
  isActive: boolean;
  createdAt: Date;
  updatedAt?: Date;
  features: Feature[]; // hydrated via join with plan_version_features
}

export interface Subscription {
  id: string;
  userId: string;
  planVersionId: string;
  startDate: Date;
  endDate?: Date;
  status: "active" | "canceled" | "expired" | "trialing";
  createdAt: Date;
  updatedAt?: Date;
}
